import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { SiteForm } from "./forms/SiteForm";
import { StudyForm } from "./forms/StudyForm";
import { TemplateForm } from "./forms/TemplateForm";
import { EditTaskForm } from "./forms/EditTaskForm";
import { UserForm } from "./forms/UserForm";

/**
 * Cluster of CRUD dialogs invoked from the AdminPanel and Dashboard.
 * Kept together to keep App.js lean.
 */
export const AppDialogs = ({
  // Edit task
  isEditTaskOpen, setIsEditTaskOpen, selectedTask, updateTask, closeTaskEditor,
  // Users
  isAddUserOpen, setIsAddUserOpen, isEditUserOpen, setIsEditUserOpen,
  selectedUser, setSelectedUser, createUser, updateUser,
  // Sites
  isAddSiteOpen, setIsAddSiteOpen, isEditSiteOpen, setIsEditSiteOpen,
  selectedSite, setSelectedSite, createSite, updateSite,
  // Studies
  isAddStudyOpen, setIsAddStudyOpen, isEditStudyOpen, setIsEditStudyOpen,
  selectedStudy, setSelectedStudy, createStudy, updateStudy,
  // Templates
  isAddTemplateOpen, setIsAddTemplateOpen, isEditTemplateOpen, setIsEditTemplateOpen,
  selectedTemplate, setSelectedTemplate, createTemplate, updateTemplate,
  // Shared
  sites, studies, users,
}) => (
  <>
    <Dialog open={isEditTaskOpen} onOpenChange={setIsEditTaskOpen}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Task</DialogTitle>
          <DialogDescription>Update task details and settings</DialogDescription>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto">
          <EditTaskForm task={selectedTask} sites={sites} studies={studies} users={users} onSave={updateTask} onCancel={closeTaskEditor} />
        </div>
      </DialogContent>
    </Dialog>

    <Dialog open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add New User</DialogTitle>
          <DialogDescription>Create a new site coordinator or study lead</DialogDescription>
        </DialogHeader>
        <UserForm sites={sites} studies={studies} onSave={(_, userData) => createUser(userData)} onCancel={() => setIsAddUserOpen(false)} />
      </DialogContent>
    </Dialog>

    <Dialog open={isEditUserOpen} onOpenChange={setIsEditUserOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>Update user details and permissions</DialogDescription>
        </DialogHeader>
        <UserForm user={selectedUser} sites={sites} studies={studies} onSave={updateUser} onCancel={() => { setIsEditUserOpen(false); setSelectedUser(null); }} isEdit={true} />
      </DialogContent>
    </Dialog>

    <Dialog open={isAddSiteOpen} onOpenChange={setIsAddSiteOpen}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Add New Site</DialogTitle>
          <DialogDescription>Create a new clinical research site</DialogDescription>
        </DialogHeader>
        <SiteForm onSave={(_, siteData) => createSite(siteData)} onCancel={() => setIsAddSiteOpen(false)} />
      </DialogContent>
    </Dialog>

    <Dialog open={isEditSiteOpen} onOpenChange={setIsEditSiteOpen}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit Site</DialogTitle>
          <DialogDescription>Update site information</DialogDescription>
        </DialogHeader>
        <SiteForm site={selectedSite} onSave={updateSite} onCancel={() => { setIsEditSiteOpen(false); setSelectedSite(null); }} isEdit={true} />
      </DialogContent>
    </Dialog>

    <Dialog open={isAddStudyOpen} onOpenChange={setIsAddStudyOpen}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Add New Study</DialogTitle>
          <DialogDescription>Create a new clinical research study</DialogDescription>
        </DialogHeader>
        <StudyForm sites={sites} onSave={(_, studyData) => createStudy(studyData)} onCancel={() => setIsAddStudyOpen(false)} />
      </DialogContent>
    </Dialog>

    <Dialog open={isEditStudyOpen} onOpenChange={setIsEditStudyOpen}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Edit Study</DialogTitle>
          <DialogDescription>Update study information and settings</DialogDescription>
        </DialogHeader>
        <StudyForm study={selectedStudy} sites={sites} onSave={updateStudy} onCancel={() => { setIsEditStudyOpen(false); setSelectedStudy(null); }} isEdit={true} />
      </DialogContent>
    </Dialog>

    <Dialog open={isAddTemplateOpen} onOpenChange={setIsAddTemplateOpen}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Template</DialogTitle>
          <DialogDescription>Create a new task template</DialogDescription>
        </DialogHeader>
        <TemplateForm onSave={(_, templateData) => createTemplate(templateData)} onCancel={() => setIsAddTemplateOpen(false)} />
      </DialogContent>
    </Dialog>

    <Dialog open={isEditTemplateOpen} onOpenChange={setIsEditTemplateOpen}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Template</DialogTitle>
          <DialogDescription>Update template settings and tasks</DialogDescription>
        </DialogHeader>
        <TemplateForm template={selectedTemplate} onSave={updateTemplate} onCancel={() => { setIsEditTemplateOpen(false); setSelectedTemplate(null); }} isEdit={true} />
      </DialogContent>
    </Dialog>
  </>
);
